import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const STORAGE_KEY = "av_lang";

const resources = {
  en: {
    translation: {
      nav: {
        home: "Home",
        about: "About",
        fleet: "Fleet",
        tours: "Tours",
        booking: "Booking",
        cta: "Book a Tour",
        tagline: "Sailing Club",
      },
      hero: {
        eyebrow: "Established 2025 · Lake Ohrid",
        title_pre: "The art of",
        title_em: "living well",
        title_post: ", under sail.",
        body: "Ars Vivendi Sailing Club invites you aboard a curated fleet of sailboats on the oldest lake in Europe. Slow mornings, golden sunsets, and the quiet rhythm of wind on water.",
        cta_book: "Book a Tour",
        cta_fleet: "View Fleet",
        scroll: "Scroll",
      },
      about: {
        eyebrow: "About the Club",
        title_pre: "A quiet rebellion",
        title_em: "hurry",
        title_join: "against",
        title_dot: ".",
        p1: "Ars Vivendi — the art of living — is our compass. Founded in 2025 on the shores of Lake Ohrid, we are a small, devoted club built around one idea: that sailing is less a sport than a way of being.",
        p2: "Our waters are ancient. Carved more than three million years ago, Lake Ohrid cradles villages older than cities and monasteries older than most nations. From our harbor, every tack is a conversation with history.",
        est_year: "Est. in Skopje",
        stat_age: "Years old lake",
        stat_boats: "Albin Vega 27s",
        stat_sunsets: "Sunsets ahead",
      },
      fleet: {
        eyebrow: "The Fleet",
        title_pre: "Two sisters.",
        title_em: "One horizon.",
        body: "A pair of Albin Vega 27 sloops — Swedish-built classics, beloved for their sea-kindly hull and quiet character. Each takes a maximum of five guests, with room to stretch out and a small cabin below.",
        guests: "Up to 5",
        type: "Albin Vega 27 · 27ft",
        stat_length: "Length overall",
        stat_built: "Vegas built worldwide",
        wind: "Average Bora wind · 12–18 knots",
      },
      tours: {
        eyebrow: "Tours & Experiences",
        title_pre: "Choose your",
        title_em: "voyage",
        title_dot: ".",
        max_guests: "Max 5 guests",
        book_aria: "Book {{name}}",
        tailor_title: "Tailor your sail",
        tailor_body: "Every tour can be tailored to suit you. Celebrate a birthday on the water, host a bachelor or bachelorette party, share a romantic sunset cruise with someone special, or take an intro-to-sailing lesson and learn the basics from the helm. Tell us what kind of day you have in mind and we'll shape the sail around it.",
        weather_title: "A note on weather",
        weather_body: "All sails are subject to wind and weather. While we follow the forecasts closely, sudden changes on the lake can force a tour to be rescheduled at short notice — sometimes without prior warning. We appreciate your understanding.",
        short_name: "Short Sail",
        short_dur: "2 hours",
        short_desc: "A relaxed two-hour sail across the bay of Ohrid, sailing past the Church of St. John at Kaneo, the red-roofed Old Town, the Presidential Villa, and Gorica Beach. Ideal for first-timers and a glass of local wine at golden hour.",
        half_name: "Half Day Sail",
        half_dur: "4 hours",
        half_desc: "A half-day voyage along the Macedonian shore — past the Church of St. John at Kaneo, the red-roofed Old Town, the Presidential Villa, and Gorica Beach, with a stop to swim and anchor at Lagadin or Cuba Libre beach.",
        full_name: "Full Day Sail",
        full_dur: "10 hours",
        full_desc: "A full day on the lake, unhurried and far-roaming. Choose a southern course — past the village of Peshtani, the prehistoric Bay of Bones, Trpejca, Zaum, and the breathtaking monastery of St. Naum — or a western course toward Struga, the Drim river, and the village of Radozhda. An optional lunch stop at one of the waterfront restaurants along the shore. Swim, eat, and listen to the long silences.",
      },
      banner: {
        eyebrow: "Lake Ohrid",
        title_pre: "Three million years of",
        title_em: "stillness",
        title_dot: ".",
        body: "Older than most mountains. Quieter than most cathedrals. The most beautiful place in the world to hoist up a sail.",
      },
      booking: {
        eyebrow: "Reserve a sail",
        title_pre: "A seat on the water,",
        title_em: "kept for you",
        title_dot: ".",
        body: "Tell us a little about your ideal day at sea and we will reply within 24 hours to confirm your booking and arrange payment.",
        port_label: "Port",
        port_value: "Ohrid Old City Port, North Macedonia",
        tel_label: "Telephone",
        email_label: "Email",
        success_title: "Request received",
        success_body: "Thank you. Our harbormaster will be in touch within 24 hours.",
        f_name: "Full name",
        f_email: "Email",
        f_phone: "Phone",
        f_guests: "Guests",
        f_tour: "Tour",
        f_date: "Preferred date",
        f_date_placeholder: "Select a date",
        f_message: "A note (optional)",
        f_message_placeholder: "Celebrating an anniversary? Dietary notes? Tell us.",
        disclaimer: "Your request is not a payment. We confirm availability first.",
        submit: "Request booking",
        submitting: "Sending",
        err_no_date: "Please select a date for your sail.",
        err_required: "Please fill in your name, email and phone.",
        err_generic: "Something went wrong. Please try again.",
        toast_success: "Booking request sent. We'll be in touch shortly.",
      },
      footer: {
        eyebrow: "Let's sail",
        title_pre: "The wind is",
        title_em: "generous",
        title_post: "in Ohrid.",
        cta: "Reserve a sail",
        port: "Port",
        tel: "Telephone",
        follow: "Follow",
        tagline: "The art of living, under sail. Established 2025 on the shores of Lake Ohrid.",
        rights: "All rights reserved.",
        est: "Est. 2025 · Ohrid, 41.1°N 20.8°E",
      },
      lang: { en: "EN", mk: "МК" },
    },
  },
  mk: {
    translation: {
      nav: {
        home: "Почетна",
        about: "За нас",
        fleet: "Флота",
        tours: "Тури",
        booking: "Резервации",
        cta: "Резервирај тура",
        tagline: "Едриличарски клуб",
      },
      hero: {
        eyebrow: "Основан 2025 · Охридско Езеро",
        title_pre: "Уметноста на",
        title_em: "убавото живеење",
        title_post: ", под едра.",
        body: "Едриличарскиот клуб Ars Vivendi ве поканува на внимателно избрана флота едрилици на најстарото езеро во Европа. Спори утра, златни залези и тивкиот ритам на ветрот врз водата.",
        cta_book: "Резервирај тура",
        cta_fleet: "Види ја флотата",
        scroll: "Скролај",
      },
      about: {
        eyebrow: "За клубот",
        title_pre: "Тивок бунт",
        title_em: "брзањето",
        title_join: "против",
        title_dot: ".",
        p1: "Ars Vivendi — уметноста на живеењето — е нашиот компас. Основан во 2025 година на бреговите на Охридското езеро, ние сме мал, посветен клуб создаден околу една идеја: дека едрењето е помалку спорт, а повеќе начин на постоење.",
        p2: "Нашите води се древни. Изградено пред повеќе од три милиони години, Охридското езеро ги милува селата постари од градовите и манастирите постари од повеќето нации. Од нашето пристаниште, секое свртување е разговор со историјата.",
        est_year: "Основан во Скопје",
        stat_age: "Години старо езеро",
        stat_boats: "Албин Вега 27",
        stat_sunsets: "Залези пред нас",
      },
      fleet: {
        eyebrow: "Флотата",
        title_pre: "Две сестри.",
        title_em: "Еден хоризонт.",
        body: "Пар едрилици Albin Vega 27 — шведски класици, омилени по нивниот стабилен труп и тивок карактер. Секоја има капацитет од најмногу 5 гости, со простор за опуштање и мала кабина под палубата.",
        guests: "До 5",
        type: "Albin Vega 27 · 27ft",
        stat_length: "Вкупна должина",
        stat_built: "Веги изградени во светот",
        wind: "Просечен северен ветар · 12–18 јазли",
      },
      tours: {
        eyebrow: "Тури и доживувања",
        title_pre: "Изберете го вашето",
        title_em: "патување",
        title_dot: ".",
        max_guests: "Најмногу 5 гости",
        book_aria: "Резервирај {{name}}",
        tailor_title: "Прилагодете го едрењето",
        tailor_body: "Секоја тура може да се прилагоди според вашиот вкус. Прославете роденден на вода, организирајте моминска или ергенска забава, споделете романтична тура на зајдисонце со некој посебен, или земете воведен час по едрење и научете ги основите. Кажете ни како замислувате идеален ден и ние ќе го обликуваме едрењето околу вас.",
        weather_title: "Забелешка за временските услови",
        weather_body: "Сите турии зависат од ветрот и временските услови. Иако внимателно ги следиме временските прогнози, ненадејни промени на езерото може да наложат презакажување без претходна најава. Ви благодариме на разбирањето.",
        short_name: "Кратко едрење",
        short_dur: "2 часа",
        short_desc: "Опуштено двочасовно едрење низ Охридскиот залив, поминувајќи покрај црквата Свети Јован Канео и старото градско јадро со црвени покриви, Претседателската вила и плажата Горица. Идеално за почетници и чаша локално вино при зајдисонце.",
        half_name: "Полудневно едрење",
        half_dur: "4 часа",
        half_desc: "Полудневно патување долж македонскиот брег — покрај црквата Свети Јован Канео и старото градско јадро, Претседателската вила и плажата Горица, со застанување за капење и сидрење кај плажата Лагадин или Cuba Libre.",
        full_name: "Целодневно едрење",
        full_dur: "10 часа",
        full_desc: "Цел ден на езерото, без брзање и со широк дофат. Изберете јужен курс — покрај селото Пештани, праисториската Залив на Коски, Трпејца, Заум и величествениот манастир Свети Наум — или западен курс кон Струга, реката Дрим и селото Радожда. По желба, застанување за ручек во некој од крајбрежните ресторани. Капете се, јадете и слушајте ја долгата тишина.",
      },
      banner: {
        eyebrow: "Охридско Езеро",
        title_pre: "Три милиони години",
        title_em: "тишина",
        title_dot: ".",
        body: "Постаро од повеќето планини. Потивко од повеќето катедрали. Најубавото место на светот да го кренете едрото.",
      },
      booking: {
        eyebrow: "Резервирајте едрење",
        title_pre: "Место на водата,",
        title_em: "чувано за вас",
        title_dot: ".",
        body: "Кажете ни малку за вашиот идеален ден на море и ние ќе одговориме во рок од 24 часа за да ја потврдиме резервацијата и да го договориме плаќањето.",
        port_label: "Пристаниште",
        port_value: "Старо Пристаниште Охрид, Северна Македонија",
        tel_label: "Телефон",
        email_label: "Е-пошта",
        success_title: "Барањето е примено",
        success_body: "Ви благодариме. Нашиот капетан ќе ве контактира во рок од 24 часа.",
        f_name: "Име и презиме",
        f_email: "Е-пошта",
        f_phone: "Телефон",
        f_guests: "Гости",
        f_tour: "Тура",
        f_date: "Сакан датум",
        f_date_placeholder: "Изберете датум",
        f_message: "Порака (по желба)",
        f_message_placeholder: "Прославувате годишнина? Алергии? Кажете ни.",
        disclaimer: "Вашето барање не е плаќање. Прво ја потврдуваме достапноста.",
        submit: "Испрати барање",
        submitting: "Се испраќа",
        err_no_date: "Изберете датум за вашата тура.",
        err_required: "Внесете го вашето име, е-пошта и телефон.",
        err_generic: "Се појави грешка. Обидете се повторно.",
        toast_success: "Барањето е испратено. Ќе ве контактираме наскоро.",
      },
      footer: {
        eyebrow: "Да едриме",
        title_pre: "Ветрот е",
        title_em: "дарежлив",
        title_post: "во Охрид.",
        cta: "Резервирај едрење",
        port: "Пристаниште",
        tel: "Телефон",
        follow: "Следете нѐ",
        tagline: "Уметноста на живеењето, под едра. Основан 2025 на бреговите на Охридското езеро.",
        rights: "Сите права задржани.",
        est: "Осн. 2025 · Охрид, 41.1°С 20.8°И",
      },
      lang: { en: "EN", mk: "МК" },
    },
  },
};

const initialLang =
  (typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY)) || "en";

i18n.use(initReactI18next).init({
  resources,
  lng: initialLang,
  fallbackLng: "en",
  interpolation: { escapeValue: false },
});

i18n.on("languageChanged", (lng) => {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
    if (typeof document !== "undefined") {
      document.documentElement.lang = lng;
    }
  } catch (_) {}
});

if (typeof document !== "undefined") {
  document.documentElement.lang = initialLang;
}

export default i18n;
